var _a;
import { useEffect, useRef } from "react";
import { clone } from "ramda";
import { SearchViewMode } from "../../hooks/types";
import { FetchStatus } from "../types";
import { getSearchStateApi } from "./search-state-api";
import { getDefaultInitialResultStatus } from "../../hooks";
import { normalizeResult, registerResultsViewedAppEvent, registerSearchPerformedAppEvent } from "./helpers";
import { useLongRunningFetcher } from "./use-long-running-fetchers";
var initialSearchOptions = {
    search: {
        criteria: {},
    },
    counts: {
        criteria: {},
    },
    result: (_a = {},
        _a[SearchViewMode.MapView] = {
            result: [],
            pageNumber: undefined,
            pageSize: undefined,
        },
        _a[SearchViewMode.ListView] = {
            result: [],
            pageNumber: undefined,
            pageSize: undefined,
        },
        _a[SearchViewMode.GalleryView] = {
            result: [],
            pageNumber: undefined,
            pageSize: undefined,
        },
        _a),
};
export var useSearchInternal = function (viewMode, criteria, result, pageNumber, pageSize, fetchData, setResult, skip, shouldSearch, shouldFetchCounts, initialResultStatus, detectLongRunningQueries, setIsLongRunning, appEvents) {
    var _a;
    var ref = useRef({
        criteria: criteria,
        result: result,
        pageNumber: pageNumber,
        pageSize: pageSize,
        fetchData: fetchData,
        setResult: setResult,
        skip: skip,
        shouldSearch: shouldSearch,
        shouldFetchCounts: shouldFetchCounts,
        recent: clone(initialSearchOptions),
        pending: clone(initialSearchOptions),
        initialResultStatus: (_a = initialResultStatus === null || initialResultStatus === void 0 ? void 0 : initialResultStatus.current) !== null && _a !== void 0 ? _a : getDefaultInitialResultStatus(),
    });
    if (!skip) {
        ref.current.criteria = criteria;
        ref.current.result = result;
        ref.current.pageNumber = pageNumber;
        ref.current.pageSize = pageSize;
        ref.current.fetchData = fetchData;
        ref.current.setResult = setResult;
        ref.current.skip = skip;
        ref.current.shouldSearch = shouldSearch;
        ref.current.shouldFetchCounts = shouldFetchCounts;
    }
    var longRunningFetcher = useLongRunningFetcher({ setIsLongRunning: setIsLongRunning, detectLongRunningQueries: detectLongRunningQueries });
    useEffect(function () {
        var _a, _b;
        if (skip) {
            return;
        }
        var viewModeSet = viewMode != null;
        var searchStateApi = getSearchStateApi(ref.current, ref.current.shouldSearch, ref.current.shouldFetchCounts, ref.current.initialResultStatus);
        var stale = function () { return !searchStateApi.searchParamsCurrent(criteria); };
        if (criteria != null && searchStateApi.shouldSearch()) {
            var searchWithDataFetcher = viewMode != null ? (_a = ref.current.fetchData.searchWithData) === null || _a === void 0 ? void 0 : _a[viewMode] : undefined;
            var searchResultWithDataSetter_1 = viewMode != null ? (_b = ref.current.setResult.searchWithData) === null || _b === void 0 ? void 0 : _b[viewMode] : undefined;
            if (viewModeSet && searchWithDataFetcher && searchResultWithDataSetter_1) {
                var stalePageOptions_1 = function () { return !searchStateApi.pageNumberAndSizeCurrent(pageNumber, pageSize); };
                searchStateApi.setPendingSearchParams(criteria, viewMode, undefined, pageNumber, pageSize);
                longRunningFetcher.started("search");
                searchWithDataFetcher(criteria, pageNumber, pageSize)
                    .then(function (result) {
                    var _a;
                    if (!stale()) {
                        longRunningFetcher.completed("search");
                        var searchResult = normalizeResult(result === null || result === void 0 ? void 0 : result.result);
                        if (appEvents === null || appEvents === void 0 ? void 0 : appEvents.dataFetchers) {
                            registerSearchPerformedAppEvent(appEvents, JSON.stringify(criteria));
                            registerResultsViewedAppEvent(appEvents, viewMode, (_a = result === null || result === void 0 ? void 0 : result.data) === null || _a === void 0 ? void 0 : _a.map(function (x) { return x["__itemId"]; }));
                        }
                        if (!stalePageOptions_1()) {
                            var pn = result.pageNumber;
                            searchStateApi.setRecentSearchParams(criteria, viewMode, searchResult, pn !== null && pn !== void 0 ? pn : pageNumber, pageSize);
                            searchResultWithDataSetter_1(FetchStatus.Success, searchResult, result === null || result === void 0 ? void 0 : result.data, pn !== pageNumber ? pn : undefined);
                        }
                        else {
                            searchStateApi.setRecentSearchParams(criteria);
                            ref.current.setResult.search(FetchStatus.Success, searchResult);
                        }
                    }
                })
                    .catch(function (e) {
                    if (!stale()) {
                        longRunningFetcher.completed("search");
                        ref.current.setResult.search(FetchStatus.Failure, undefined, e);
                    }
                });
            }
            else {
                searchStateApi.setPendingSearchParams(criteria);
                longRunningFetcher.started("search");
                ref.current.fetchData
                    .search(criteria)
                    .then(function (result) {
                    (appEvents === null || appEvents === void 0 ? void 0 : appEvents.dataFetchers) && registerSearchPerformedAppEvent(appEvents, JSON.stringify(criteria));
                    if (!stale()) {
                        longRunningFetcher.completed("search");
                        searchStateApi.setRecentSearchParams(criteria);
                        ref.current.setResult.search(FetchStatus.Success, normalizeResult(result));
                    }
                })
                    .catch(function (e) {
                    if (!stale()) {
                        longRunningFetcher.completed("search");
                        ref.current.setResult.search(FetchStatus.Failure, undefined, e);
                    }
                });
            }
        }
        if (criteria != null && searchStateApi.shouldFetchCounts()) {
            searchStateApi.setPendingFetchCountsParams(criteria);
            longRunningFetcher.started("count");
            ref.current.fetchData
                .counts(criteria)
                .then(function (result) {
                if (!stale()) {
                    longRunningFetcher.completed("count");
                    searchStateApi.setRecentFetchCountsParams(criteria);
                    ref.current.setResult.counts(FetchStatus.Success, result);
                }
            })
                .catch(function (e) {
                if (!stale()) {
                    longRunningFetcher.completed("count");
                    ref.current.setResult.counts(FetchStatus.Failure, undefined, e);
                }
            });
        }
        var staleResult = function () { return !searchStateApi.fetchDataParamsCurrent(result, pageNumber, pageSize); };
        if (viewModeSet && searchStateApi.isSearchCompleted(criteria) && searchStateApi.shouldFetchData(viewMode)) {
            var dataFetcher = ref.current.fetchData.data[viewMode];
            var setResult_1 = ref.current.setResult.data[viewMode];
            if (dataFetcher && setResult_1 && result) {
                searchStateApi.setPendingFetchDataParams(viewMode, result, pageNumber, pageSize);
                longRunningFetcher.started("data");
                dataFetcher(result, pageNumber, pageSize)
                    .then(function (res) {
                    var _a;
                    if (!staleResult()) {
                        longRunningFetcher.completed("data");
                        if (!stale()) {
                            var pn = res.pageNumber;
                            searchStateApi.setRecentFetchDataParams(viewMode, result, pn !== null && pn !== void 0 ? pn : pageNumber, pageSize);
                            (appEvents === null || appEvents === void 0 ? void 0 : appEvents.dataFetchers) &&
                                registerResultsViewedAppEvent(appEvents, viewMode, (_a = res === null || res === void 0 ? void 0 : res.data) === null || _a === void 0 ? void 0 : _a.map(function (x) { return x["__itemId"]; }));
                            setResult_1(FetchStatus.Success, res.data, pn !== pageNumber ? pn : undefined);
                        }
                    }
                })
                    .catch(function (e) {
                    if (!staleResult()) {
                        longRunningFetcher.completed("data");
                        if (!stale()) {
                            setResult_1(FetchStatus.Failure, undefined, undefined, e);
                        }
                    }
                });
            }
        }
    }, [
        viewMode,
        criteria,
        result,
        pageNumber,
        pageSize,
        skip,
        longRunningFetcher,
        appEvents === null || appEvents === void 0 ? void 0 : appEvents.dataFetchers,
        appEvents,
    ]);
};
//# sourceMappingURL=use-search.js.map